import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ChevronUp, 
  ChevronDown, 
  X, 
  Upload, 
  CheckCircle, 
  AlertCircle, 
  Clock,
  FileVideo,
  Play,
  Trash2,
  Timer
} from 'lucide-react';
import { useUpload } from '../contexts/UploadContext';
import Tooltip from './Tooltip';

const FloatingUploadWidget: React.FC = () => {
  const navigate = useNavigate();
  const { tasks, removeTask, cancelTask, hasActiveTasks } = useUpload();
  const [isExpanded, setIsExpanded] = useState(true);
  const [isMinimized, setIsMinimized] = useState(false);
  const [completionTimes, setCompletionTimes] = useState<Record<string, number>>({});
  const [maxProgressTracker, setMaxProgressTracker] = useState<Record<string, number>>({});
  const [taskPhases, setTaskPhases] = useState<Record<string, 'uploading' | 'analyzing'>>({});

  // Memoize task counts to prevent unnecessary recalculations
  const taskCounts = useMemo(() => {
    const active = tasks.filter(task => 
      task.status === 'uploading' || task.status === 'analyzing'
    );
    const completed = tasks.filter(task => 
      task.status === 'completed'
    );
    const error = tasks.filter(task => 
      task.status === 'error'
    );
    
    return { active, completed, error };
  }, [tasks]);

  // Track phase transitions to reset progress appropriately
  useEffect(() => {
    const updates: Record<string, 'uploading' | 'analyzing'> = {};
    const progressResets: Record<string, number> = {};
    let hasUpdates = false;
    let hasProgressResets = false;

    tasks.forEach(task => {
      const currentPhase = taskPhases[task.id];
      const newPhase = task.status as 'uploading' | 'analyzing';
      
      // Detect phase transition from uploading to analyzing
      if (currentPhase === 'uploading' && newPhase === 'analyzing') {
        // console.log(`🔄 [FloatingWidget] Phase transition detected for ${task.fileName}: uploading → analyzing`);
        updates[task.id] = newPhase;
        progressResets[task.id] = 0; // Reset progress for analysis phase
        hasUpdates = true;
        hasProgressResets = true;
      } else if (currentPhase !== newPhase && (newPhase === 'uploading' || newPhase === 'analyzing')) {
        updates[task.id] = newPhase;
        hasUpdates = true;
      }
    });

    if (hasUpdates) {
      setTaskPhases(prev => ({ ...prev, ...updates }));
    }

    if (hasProgressResets) {
      setMaxProgressTracker(prev => ({ ...prev, ...progressResets }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks.map(t => `${t.id}:${t.status}`).join('|'), taskPhases]);

  // Memoize task processing to prevent flickering and optimize performance
  const processedTasks = useMemo(() => {
    return tasks.map(task => {
      let displayProgress = task.progress;
      
      // Ensure progress is a valid number
      if (typeof displayProgress !== 'number' || isNaN(displayProgress)) {
        displayProgress = 0;
      }
      
      const currentPhase = taskPhases[task.id];
      
      // For upload phase (status: 'uploading'), show actual chunk progress 0-100%
      if (task.status === 'uploading') {
        displayProgress = Math.min(Math.max(displayProgress, 0), 100);
      }
      
      // For analyzing phase, show analysis progress 0-100% (reset from upload)
      else if (task.status === 'analyzing') {
        // Use the progress that's already been parsed by UploadContext
        // The UploadContext correctly extracts progress from error_message field
        displayProgress = Math.min(Math.max(displayProgress, 0), 100);
      }
      
      // For completed tasks, always show 100%
      else if (task.status === 'completed') {
        displayProgress = 100;
      }
      
      // For error state, keep current progress
      else if (task.status === 'error') {
        displayProgress = Math.min(Math.max(displayProgress, 0), 100);
      }
      
      // Apply max progress tracking only for uploading tasks to prevent backwards movement
      // For analyzing tasks, use the exact progress from the backend to stay in sync with video page
      const taskId = task.id;
      const currentMaxProgress = maxProgressTracker[taskId] || 0;
      let newProgress = displayProgress;
      
      // Only apply max progress tracking for uploading tasks
      if (task.status === 'uploading') {
        // If we're in the same phase, ensure progress doesn't go backwards
        if (currentPhase === task.status) {
          newProgress = Math.max(displayProgress, currentMaxProgress);
        } else {
          // Phase transition - allow the new progress value
          newProgress = displayProgress;
        }
      } else if (task.status === 'analyzing') {
        // For analyzing tasks, always use the exact progress from backend
        // This ensures the widget stays in sync with the video page
        newProgress = displayProgress;
        
        // Debug logging for analyzing tasks to track synchronization
        if (displayProgress !== currentMaxProgress && displayProgress > 0) {
          // console.log(`📊 [FloatingWidget] Analyzing progress for ${task.fileName}: ${displayProgress}% (backend sync)`);
        }
      }
      
      const processedTask = {
        ...task,
        displayProgress: Math.round(newProgress) // Round to prevent decimal flickering
      };
      
      return processedTask;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, taskPhases]);

  // Separate effect to update max progress tracker without causing render loops
  useEffect(() => {
    const updates: Record<string, number> = {};
    let hasUpdates = false;
    
    tasks.forEach(task => {
      // Only track max progress for uploading tasks
      if (task.status === 'uploading') {
        const currentMaxProgress = maxProgressTracker[task.id] || 0;
        const currentPhase = taskPhases[task.id];
        let taskProgress = Math.min(Math.max(task.progress, 0), 100);
        
        // Only update if we're in the uploading phase and progress has increased
        // OR if we're transitioning to uploading phase
        if (currentPhase === 'uploading') {
          // Same phase - only increase progress
          if (taskProgress > currentMaxProgress) {
            updates[task.id] = taskProgress;
            hasUpdates = true;
          }
        } else {
          // Phase transition to uploading - reset to current progress
          updates[task.id] = taskProgress;
          hasUpdates = true;
        }
      }
    });
    
    if (hasUpdates) {
      setMaxProgressTracker(prev => ({
        ...prev,
        ...updates
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks.map(t => `${t.id}:${t.status}:${t.progress}`).join('|'), taskPhases]);

  // Memoize callbacks to prevent unnecessary re-renders
  const handleToggleExpanded = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  const handleMinimize = useCallback(() => {
    setIsMinimized(true);
  }, []);

  const handleRestore = useCallback(() => {
    setIsMinimized(false);
  }, []);

  const handleNavigateToVideo = useCallback((videoId: string) => {
    navigate(`/videos/${videoId}`);
  }, [navigate]);

  const handleNavigateHome = useCallback(() => {
    navigate('/');
  }, [navigate]);

  // Track when tasks complete to show countdown
  useEffect(() => {
    const newlyCompleted = tasks.filter(task => 
      task.status === 'completed' && !completionTimes[task.id]
    );
    
    if (newlyCompleted.length > 0) {
      const newTimes: Record<string, number> = {};
      newlyCompleted.forEach(task => {
        newTimes[task.id] = Date.now();
      });
      
      setCompletionTimes(prev => ({ ...prev, ...newTimes }));
    }
    
    // Clean up completion times for removed tasks in a separate effect to avoid dependency cycle
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks]); // Only depend on tasks, not completionTimes

  // Separate effect for cleaning up completion times to avoid dependency cycles
  useEffect(() => {
    const taskIds = new Set(tasks.map(t => t.id));
    setCompletionTimes(prev => {
      const filtered: Record<string, number> = {};
      let hasChanges = false;
      
      Object.keys(prev).forEach(id => {
        if (taskIds.has(id)) {
          filtered[id] = prev[id];
        } else {
          hasChanges = true;
        }
      });
      
      // Only update if there are actual changes
      return hasChanges ? filtered : prev;
    });
  }, [tasks]);

  // Update countdown display every second - only for completed tasks
  useEffect(() => {
    const hasCompletedTasks = tasks.some(task => task.status === 'completed');
    if (!hasCompletedTasks) return;

    const interval = setInterval(() => {
      // Trigger re-render for countdown display by updating a dummy value
      setCompletionTimes(prev => ({ ...prev }));
    }, 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks.some(task => task.status === 'completed')]); // Only re-run when completed task status changes

  // Clean up progress tracker when tasks are removed
  useEffect(() => {
    const taskIds = new Set(tasks.map(t => t.id));
    setMaxProgressTracker(prev => {
      const filtered: Record<string, number> = {};
      let hasChanges = false;
      
      Object.keys(prev).forEach(id => {
        if (taskIds.has(id)) {
          filtered[id] = prev[id];
        } else {
          hasChanges = true;
        }
      });
      
      // Only update if there are actual changes
      return hasChanges ? filtered : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks.map(t => t.id).join(',')]); // Only depend on task IDs

  // Reset progress tracker for completed tasks (so they can restart if re-uploaded)
  useEffect(() => {
    const completedTaskIds = tasks
      .filter(task => task.status === 'completed' || task.status === 'error')
      .map(task => task.id);
    
    if (completedTaskIds.length > 0) {
      setMaxProgressTracker(prev => {
        const updated = { ...prev };
        let hasChanges = false;
        
        completedTaskIds.forEach(id => {
          const task = tasks.find(t => t.id === id && t.status === 'completed');
          if (task && prev[id] !== 100) {
            updated[id] = 100; // Ensure completed tasks show 100%
            hasChanges = true;
          }
        });
        
        return hasChanges ? updated : prev;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks.filter(task => task.status === 'completed' || task.status === 'error').map(t => t.id).join(',')]);  // Only depend on completed/error task IDs

  // Don't show the widget if there are no tasks
  if (tasks.length === 0) return null;

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusIcon = (task: any) => {
    switch (task.status) {
      case 'uploading':
        // Check if this is a URL import task
        const isUrlImport = task.source === 'url';
        
        if (isUrlImport) {
          return <Upload className="h-4 w-4 text-red-600 animate-pulse" />;
        }
        return <Upload className="h-4 w-4 text-red-600" />;
      case 'analyzing':
        return <Clock className="h-4 w-4 text-gray-600 animate-pulse" />;
      case 'completed':
        // Show timer icon if counting down to auto-removal
        const completionTime = completionTimes[task.id];
        if (completionTime) {
          const elapsed = Date.now() - completionTime;
          const remaining = Math.max(0, 20000 - elapsed);
          const secondsLeft = Math.ceil(remaining / 1000);
          
          if (secondsLeft <= 10 && secondsLeft > 0) {
            return <Timer className="h-4 w-4 text-red-500 animate-pulse" />;
          }
        }
        return <CheckCircle className="h-4 w-4 text-red-600" />;
      case 'error':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <FileVideo className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'uploading':
        return 'from-red-500 to-red-600';
      case 'analyzing': 
        return 'from-red-500 to-red-600';
      case 'completed':
        return 'from-gray-900 to-black';
      case 'error':
        return 'from-red-500 to-red-600';
      default:
        return 'from-gray-400 to-gray-500';
    }
  };

  const getStatusMessage = (task: any) => {
    if (task.status === 'uploading') {
      // Check if this is a URL import task
      const isUrlImport = task.source === 'url';
      
      if (isUrlImport) {
        const platform = task.platform === 'youtube' ? 'YouTube' : task.platform === 'twitch' ? 'Twitch' : 'URL';
        return `Downloading from ${platform}...`;
      }
      return 'Uploading...';
    } else if (task.status === 'analyzing') {
      return task.processingStatus || 'Analyzing video...';
    } else if (task.status === 'completed') {
      // Show countdown for completed tasks
      const completionTime = completionTimes[task.id];
      if (completionTime) {
        const elapsed = Date.now() - completionTime;
        const remaining = Math.max(0, 20000 - elapsed); // 20 seconds total
        const secondsLeft = Math.ceil(remaining / 1000);
        
        if (secondsLeft > 5) {
          return `Completed! Auto-hiding in ${secondsLeft}s`;
        } else if (secondsLeft > 0) {
          return `Auto-hiding in ${secondsLeft}s...`;
        } else {
          return 'Removing...';
        }
      }
      return 'Completed!';
    }
    return task.uploadStatus;
  };

  if (isMinimized) {
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <button
          onClick={handleRestore}
          className="bg-white border border-gray-200 rounded-full p-3 shadow-lg hover:shadow-xl transition-all duration-200 group"
        >
          <div className="flex items-center space-x-2">
            {hasActiveTasks ? (
              <div className="flex items-center space-x-1">
                <Upload className="h-5 w-5 text-red-600 animate-pulse" />
                <span className="text-sm font-medium text-gray-700">
                  {taskCounts.active.length}
                </span>
              </div>
            ) : (
              <CheckCircle className="h-5 w-5 text-red-600" />
            )}
          </div>
        </button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-sm w-full">
      <div className="bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden animate-fade-up">
        {/* Header */}
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <FileVideo className="h-5 w-5 text-gray-600" />
              <h3 className="font-semibold text-gray-900">
                Uploads ({tasks.length})
              </h3>
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={handleToggleExpanded}
                className="p-1 hover:bg-gray-200 rounded transition-colors"
              >
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-gray-600" />
                ) : (
                  <ChevronUp className="h-4 w-4 text-gray-600" />
                )}
              </button>
              <button
                onClick={handleMinimize}
                className="p-1 hover:bg-gray-200 rounded transition-colors"
              >
                <X className="h-4 w-4 text-gray-600" />
              </button>
            </div>
          </div>

          {/* Summary */}
          <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
            {taskCounts.active.length > 0 && (
              <span className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                <span>{taskCounts.active.length} active</span>
              </span>
            )}
            {taskCounts.completed.length > 0 && (
              <span className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-red-600 rounded-full"></div>
                <span>{taskCounts.completed.length} completed</span>
              </span>
            )}
            {taskCounts.error.length > 0 && (
              <span className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                <span>{taskCounts.error.length} failed</span>
              </span>
            )}
          </div>
        </div>

        {/* Task List */}
        {isExpanded && (
          <div className="max-h-80 overflow-y-auto">
            {processedTasks.slice().reverse().map((task) => (
              <div key={task.id} className="p-4 border-b border-gray-100 last:border-b-0">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-2 flex-1 min-w-0">
                    {getStatusIcon(task)}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {task.fileName}
                      </p>
                      {!(task.source === 'url' && task.fileSize === 0) && (
                        <p className="text-xs text-gray-500">
                          {formatFileSize(task.fileSize)}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-1 ml-2">
                    {task.status === 'completed' && task.videoId && (() => {
                      const videoId = task.videoId;
                      return (
                        <Tooltip
                          content="View video"
                          position="top"
                        >
                          <button
                            onClick={() => handleNavigateToVideo(videoId)}
                            className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                          >
                            <Play className="h-3 w-3" />
                          </button>
                        </Tooltip>
                      );
                    })()}
                    
                    {task.canCancel && (task.status === 'uploading' || task.status === 'analyzing') && (
                      <Tooltip
                        content="Cancel"
                        position="top"
                      >
                        <button
                          onClick={() => cancelTask(task.id)}
                          className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Tooltip>
                    )}
                    
                    {(task.status === 'completed' || task.status === 'error') && (
                      <Tooltip
                        content="Remove"
                        position="top"
                      >
                        <button
                          onClick={() => removeTask(task.id)}
                          className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </Tooltip>
                    )}
                  </div>
                </div>

                {/* Progress bar for active tasks */}
                {task.status === 'uploading' && (
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs text-gray-600">
                      <span>
                        {getStatusMessage(task)}
                      </span>
                      <span>{task.displayProgress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-lg h-1.5">
                      <div 
                        className={`h-1.5 bg-gradient-to-r ${getStatusColor(task.status)} transition-all duration-500 ease-out ${task.displayProgress >= 100 ? 'rounded-lg' : 'rounded-l-lg'}`}
                        style={{ width: `${task.displayProgress}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Progress bar for analyzing tasks */}
                {task.status === 'analyzing' && (
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs text-gray-600">
                      <span>
                        {getStatusMessage(task)}
                      </span>
                      <span className="text-red-600 font-medium">{task.displayProgress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-lg h-1.5">
                      <div 
                        className={`h-1.5 bg-gradient-to-r ${getStatusColor(task.status)} transition-all duration-500 ease-out ${task.displayProgress >= 100 ? 'rounded-lg' : 'rounded-l-lg'}`}
                        style={{ width: `${task.displayProgress}%` }}
                      />
                    </div>
                    <div className="flex items-center text-xs text-gray-500 mt-1">
                      <div className="flex space-x-1 mr-2">
                        <div className="w-1 h-1 bg-gray-500 rounded-full animate-bounce"></div>
                        <div className="w-1 h-1 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                        <div className="w-1 h-1 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      </div>
                      <span className="text-xs">AI analyzing video content...</span>
                    </div>
                  </div>
                )}

                {/* Status messages */}
                {task.status === 'completed' && (
                  <p className="text-xs text-gray-700 mt-1">
                    ✓ {task.uploadStatus || 'Analysis complete - Ready to view clips'}
                  </p>
                )}
                
                {task.status === 'error' && task.error && (
                  <p className="text-xs text-red-600 mt-1">
                    ✗ {task.error}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Footer Actions */}
        {taskCounts.completed.length > 0 && (
          <div className="bg-gray-50 px-4 py-2 border-t border-gray-200">
            <button
              onClick={handleNavigateHome}
              className="text-xs text-primary-600 hover:text-primary-700 font-medium"
            >
              View all videos →
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FloatingUploadWidget; 